/**
 * Which body a villager wears, and what he is carrying while he wears it.
 *
 * The art pipeline bakes a villager as **two bodies × seven errands**, plus a
 * carrying sheet per haulable good. `render/spritedrop.ts` looks a sheet up by
 * unit-type id and nothing else, which is right for a soldier — every Ashfield
 * Spear is the same picture — and wrong for the one unit type that is nineteen
 * thousand different people. So villagers get their sheet from here instead.
 *
 * ── Everything this decides is already hashed ───────────────────────────────
 *
 * There is no new state and there could not be one: the sheet is a pure
 * function of the mouth (`mouthOf`, a hash of the slot), the errand (`errandOf`,
 * a hash of the slot and the tick), and the load — `UnitStore.carry` and
 * `carryKind`, which the node system added and which are hashed and saved. So a
 * villager looks the same after a reload and in a replay, for the same reason
 * he says the same thing, and the renderer stores nothing.
 *
 * **`mouthOf` is the single authority on which body a slot wears.** It lives in
 * `render/chatter.ts` because the bubbles needed it first, and it is imported
 * rather than re-derived here on purpose: two hashes for "is this one a man"
 * would eventually disagree, and a villager whose body and voice disagreed is
 * exactly the bug nobody would think to look for.
 *
 * ── The load comes first, and why ───────────────────────────────────────────
 *
 * A hauler is drawn carrying whatever is in his arms *whatever errand he is
 * on*. That is not a shortcut — a man walking home under a load of timber is
 * doing the most legible thing in the settlement, and it is the whole of what
 * the brief asked for when it said the worker should be seen taking materials
 * back. A basket that only showed while `Errand.Work` was current would blink
 * out for the walk home, which is the only part of the trip worth drawing.
 */

import { Errand, type ErrandId } from '../sim/systems/villagers.ts';
import { RESOURCE_KEYS, UnitState } from '../sim/types.ts';
import { Mouth, mouthOf, type MouthId } from './chatter.ts';
import { unitSheet, type UnitSheet } from './spritedrop.ts';

/**
 * The goods a villager has a carrying pose for.
 *
 * Four, and it is not an oversight that there are not eighteen. These are the
 * goods that come off a *node* — timber, stone, iron — plus grain off a field,
 * which is the other thing anyone is ever seen carrying. Everything else in
 * `RESOURCE_KEYS` is made indoors and moves as a number.
 */
const CARRY_ART: Record<string, string> = {
  timber: 'carry_timber',
  stone: 'carry_stone',
  iron: 'carry_iron',
  grain: 'carry_grain',
};

/** Errand → the word the bake used. Kept beside `moodOf` in spirit, not in code. */
const ERRAND_ART: Record<number, string> = {
  [Errand.Work]: 'work',
  [Errand.Pray]: 'pray',
  [Errand.Gather]: 'gather',
  [Errand.Talk]: 'talk',
  [Errand.Rest]: 'rest',
  [Errand.Wander]: 'walk',
};

/**
 * The sheet id for this villager, right now.
 *
 * `carryKind` is a `RESOURCE_KEYS` index **plus one**, so zero means empty
 * hands — the store's own comment says so, and getting that off by one would
 * put every idle villager under a load of timber.
 */
export function villagerSheetId(
  i: number,
  errand: ErrandId,
  state: number,
  carry: number,
  carryKind: number,
): string {
  const body = mouthOf(i) === Mouth.Man ? 'm' : 'f';

  if (carry > 0 && carryKind > 0) {
    const good = RESOURCE_KEYS[carryKind - 1];
    const pose = CARRY_ART[good];
    if (pose !== undefined) return `villager_${body}_${pose}`;
  }

  // Standing about with nothing to do is a different picture from walking to
  // somewhere, and the sim already distinguishes them.
  if (state === UnitState.Moving) return `villager_${body}_walk`;
  return `villager_${body}_${ERRAND_ART[errand] ?? 'idle'}`;
}

/**
 * The sheet, or null while it is missing or still loading.
 *
 * Falls back through **body → plain villager → nothing**, and the middle rung
 * matters: `unitSheet` caches a miss, so a pose that was never baked would
 * otherwise leave that villager as a vector body among sprite neighbours, which
 * reads as a rendering fault rather than as missing art. One generic
 * `villager.png` behind the whole set means an unbaked pose degrades to the
 * right *kind* of person rather than to a different art style.
 */
export function villagerSheet(
  i: number,
  errand: ErrandId,
  state: number,
  carry: number,
  carryKind: number,
): UnitSheet | null {
  return (
    unitSheet(villagerSheetId(i, errand, state, carry, carryKind)) ??
    unitSheet(`villager_${mouthOf(i) === Mouth.Man ? 'm' : 'f'}_idle`) ??
    unitSheet('villager')
  );
}

export { Mouth, type MouthId };
